/* ──────────────────────────────────────────────────────────────
   Site/index.tsx · Live public catalog preview
   Lists PUBLISHED items from the public, customer-facing API and lets you
   open one for its resolved detail. (sk-catalog-api /public/items)
   ────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useCallback } from 'react';
import { useReloadOnFocus } from '../navigation/useReloadOnFocus';
import { View, Text, ScrollView, TouchableOpacity, ActivityIndicator, Image, Platform, ViewStyle } from 'react-native';

import { useTheme } from '../context/themeContext';
import { useResponsive } from '../lib/responsive';
import { Icon, Button, Chip, Empty } from '../components/ui';
import { getPublicItems, getPublicItem } from '../services/catalog/publicCatalog';
import { getItemTypes } from '../services/catalog/itemType';
import { handleApiError } from '../utils/errorHandler';
import { asMoney, formatMoney } from '../utils/money';
import type { PublicItem } from '../schemas/catalog/publicCatalog';

export default function SiteView() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows, font } = theme;
  const { isMobile } = useResponsive();

  const [items, setItems] = useState<PublicItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<PublicItem | null>(null);
  const [typeMap, setTypeMap] = useState<Record<string, string>>({});

  const typeName = (it: any) => typeMap[it?.itemTypeId] || 'Item';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Load item types so each card can show which type it came from.
      try {
        const t = await getItemTypes();
        const map: Record<string, string> = {};
        (t.items ?? []).forEach((ty: any) => { map[ty.id] = ty.name; });
        setTypeMap(map);
      } catch { /* non-fatal */ }

      // The public list already carries each item's full `fields` bundle
      // (name, price, media cdn_url — see publicCatalogService.list), so the
      // grid renders straight from it. Do NOT fetch per-item detail here: that
      // was an O(items) request storm (one GET /public/items/:id per card) that
      // saturated the browser's connection pool. Detail is fetched lazily in
      // openDetail() only when a card is actually opened.
      const res = await getPublicItems({ limit: 100 });
      setItems((res as any).items ?? []);
    } catch (err) {
      handleApiError(err, 'Could not load the public catalog');
    } finally {
      setLoading(false);
    }
  }, []);

  useReloadOnFocus(load);

  const openDetail = async (it: PublicItem) => {
    try {
      const res = await getPublicItem(it.id);
      setDetail((res as any).data ?? res);
    } catch (err) {
      handleApiError(err, 'Could not load item');
    }
  };

  const title = (it: any) =>
    it.name || (it.fields && (it.fields.title || it.fields.name)) || it.skuOrReference || it.ref || it.id?.slice(0, 8);
  /**
   * CC-002 CR-3 — the storefront preview must show exactly what a customer's
   * client would see, and that is now `{ amount, currency }`. `asMoney` also
   * accepts the older bare-number form, so this keeps working against an
   * un-upgraded API.
   */
  const priceOf = (it: any) => asMoney(it.fields?.price ?? it.fields?.base_price);
  // A field value → a usable image URL. Accepts extension-less CDN urls,
  // media objects {cdnUrl|url}, and arrays of them.
  const pickImg = (v: any): string | null => {
    if (!v) return null;
    if (typeof v === 'string') return /^https?:\/\//i.test(v) ? v : null;
    if (Array.isArray(v)) return pickImg(v[0]);
    if (typeof v === 'object') return v.cdnUrl || v.url || null;
    return null;
  };
  const imageOf = (it: any) => {
    if (Array.isArray(it.media) && it.media[0]?.url) return it.media[0].url;
    const f = it.fields || {};
    // Prefer common media keys, then any field that resolves to a url.
    for (const k of ['photo', 'image', 'images', 'media', 'thumbnail', 'picture', 'cover']) {
      const r = pickImg(f[k]); if (r) return r;
    }
    for (const k of Object.keys(f)) { const r = pickImg(f[k]); if (r) return r; }
    return null;
  };

  const card: ViewStyle = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, overflow: 'hidden', ...(shadows.sm as ViewStyle) };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.paper }} contentContainerStyle={{ padding: isMobile ? 16 : 24, paddingBottom: 64 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, rowGap: 10, marginBottom: 8 }}>
        <View style={{ flexShrink: 1, flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <Icon name="globe" size={18} color={colors.accent} />
          <Text style={{ fontSize: fontSize['2xl'], fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>Storefront preview</Text>
          <Chip tone="success" dot>Live</Chip>
        </View>
        <Button size="sm" icon="refresh" onPress={load}>Refresh</Button>
      </View>
      <Text style={{ fontSize: fontSize.md, color: colors.ink3, fontFamily: font.sans, marginBottom: 20 }}>
        Exactly what customers see — your PUBLISHED items, resolved for the public catalog API.
      </Text>

      {loading ? (
        <View style={{ paddingVertical: 48, alignItems: 'center' }}><ActivityIndicator color={colors.accent} /></View>
      ) : items.length === 0 ? (
        <Empty icon={<Icon name="box" size={28} color={colors.ink4} />} title="Nothing published yet">
          Publish an item from the editor to see it here.
        </Empty>
      ) : (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
          {items.map(it => (
            <TouchableOpacity key={it.id} activeOpacity={0.85} onPress={() => openDetail(it)} style={[card, { width: isMobile ? '100%' : 240, ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}) }]}>
              <View style={{ height: 120, backgroundColor: colors.paper3, alignItems: 'center', justifyContent: 'center' }}>
                {imageOf(it) ? (
                  <Image source={{ uri: imageOf(it) }} style={{ width: '100%', height: '100%' } as any} resizeMode="cover" />
                ) : (
                  <Icon name="box" size={28} color={colors.ink4} />
                )}
              </View>
              <View style={{ padding: 12 }}>
                <View style={{ alignSelf: 'flex-start', marginBottom: 6 }}>
                  <Chip tone="neutral">{typeName(it)}</Chip>
                </View>
                <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }} numberOfLines={1}>{title(it)}</Text>
                {priceOf(it) != null && (
                  <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontFamily: font.mono, writingDirection: 'ltr', marginTop: 4 }}>
                    {formatMoney(priceOf(it))}
                  </Text>
                )}
                <Text style={{ fontSize: fontSize.xs, color: colors.ink4, fontFamily: font.mono, marginTop: 4 }} numberOfLines={1}>{(it as any).skuOrReference || ''}</Text>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {detail && (
        <View style={[card, { marginTop: 20, padding: 16 }]}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>{title(detail)}</Text>
              <Chip tone="neutral">{typeName(detail)}</Chip>
            </View>
            <TouchableOpacity onPress={() => setDetail(null)}><Icon name="x" size={16} color={colors.ink3} /></TouchableOpacity>
          </View>

          {/* Bundle contents — shown when the public API embeds bundle details. */}
          {detail.bundle?.components?.length ? (
            <View style={{ marginBottom: 12, borderWidth: 1, borderColor: colors.line, borderRadius: 8, overflow: 'hidden' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 10, paddingHorizontal: 12, backgroundColor: colors.paper2, borderBottomWidth: 1, borderBottomColor: colors.line }}>
                <Icon name="layers" size={14} color={colors.ink2} />
                <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>
                  Bundle contents
                </Text>
                <Chip tone="neutral">{detail.bundle.components.length}</Chip>
              </View>
              {detail.bundle.components.map((c, i) => {
                // CC-002 CR-3 — the API sends the per-unit price as money; the
                // line total is unit × qty, and multiplying money means working
                // in MINOR units (integers) and never in floats.
                const childId = c.childItemId ?? c.itemId ?? '';
                const unit = c.unitPrice ?? c.price ?? null;
                const qty = Number(c.quantity ?? 1) || 1;
                const lineTotal = unit
                  ? { amount: String(Math.round(Number(unit.amount) * qty)), currency: unit.currency }
                  : null;
                return (
                  <View key={childId || i} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingVertical: 9, paddingHorizontal: 12, borderTopWidth: i === 0 ? 0 : 1, borderTopColor: colors.line }}>
                    <Text numberOfLines={1} style={{ flex: 1, fontSize: fontSize.sm, color: colors.ink, fontFamily: font.sans }}>
                      {c.name || c.ref || childId.slice(0, 8)} <Text style={{ color: colors.ink3 }}>×{qty}</Text>
                    </Text>
                    <Text style={{ fontFamily: font.mono, writingDirection: 'ltr', fontSize: fontSize.sm, color: lineTotal == null ? colors.ink3 : colors.ink2 }}>
                      {lineTotal == null ? 'On enquiry' : formatMoney(lineTotal)}
                    </Text>
                  </View>
                );
              })}
              {/* The rollup the API computed. Preferred over summing the lines
                  locally: the server knows which edges are dynamic and whether
                  the bundle's price is pinned, and a locally-summed total would
                  disagree with it for a static or overridden bundle. */}
              {(detail.bundle.totalPrice ?? detail.bundle.partsTotal) != null && (
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10, paddingHorizontal: 12, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.paper2 }}>
                  <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontFamily: font.sans }}>Total from parts</Text>
                  <Text style={{ fontFamily: font.mono, writingDirection: 'ltr', fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink }}>
                    {formatMoney(detail.bundle.totalPrice ?? detail.bundle.partsTotal)}
                  </Text>
                </View>
              )}
            </View>
          ) : null}

          <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink2 }} numberOfLines={16}>
            {JSON.stringify(detail.fields ?? detail, null, 2).slice(0, 1200)}
          </Text>
        </View>
      )}
    </ScrollView>
  );
}
